import { useState } from "react"

import {
  Card,
  BlockStack,
  InlineGrid,
  Box,
  InlineStack,
  Button,
  Badge,
  Text,
  TextField,
  Link,
  ButtonGroup,
  Icon,
  Checkbox,
  Divider,
  Collapsible,
  Select,
  RadioButton,
  Image,
  Tag,
  ChoiceList,
  useBreakpoints,
} from "@shopify/polaris";

import {
  ChevronUpIcon,
  ChevronDownIcon,
  InfoIcon,
  XSmallIcon,
  LinkIcon,
  ImagesIcon,
  CashDollarIcon,
  TextAlignLeftIcon,
  CheckIcon,
  ContractIcon,
  FormsIcon,
  PageAddIcon
} from '@shopify/polaris-icons'
import { usePartnerUp } from "../../providers/PartnerUpStoreProvider";
import InstructionsRichTextEditor from "../InstructionsRichTextEditor";
import { contentFormats } from "../atoms";

function getRandId() {
  return Math.random().toString(36).substring(7)
}

export default function CampaignFields({ localCampaign, handleLocalCampaignChange }) {
  const { uploadFile } = usePartnerUp()
  const { mdUp } = useBreakpoints();
  const [openSection, setOpenSection] = useState('details')
  const [uploadLoading, setUploadLoading] = useState(false)

  return (
    <BlockStack gap="500">
      <Card>
        <BlockStack gap="500">
          <Box onClick={() => openSection === 'details' ? setOpenSection(null) : setOpenSection('details')}>
            <BlockStack gap="100">
              <InlineStack align="space-between">
                <InlineStack gap="100" blockAlign="center">
                  <InlineStack>
                    <Icon source={InfoIcon} color="success" />
                  </InlineStack>
                  <Text variant="headingMd">Campaign Details</Text>
                </InlineStack>
                <Button icon={openSection === 'details' ? ChevronUpIcon : ChevronDownIcon} />
              </InlineStack>
            </BlockStack>
          </Box>
          <Collapsible open={openSection === 'details'} id="details">
            <BlockStack gap="800">
              <InlineGrid columns={{ xs: "1", md: "2" }} gap="500">
                <BlockStack gap="300">
                  <TextField label="Internal Label" value={localCampaign?.campaignTitle} onChange={v => handleLocalCampaignChange('campaignTitle', v)} helpText="Only visible to you" />
                  <TextField label="Public Title" value={localCampaign?.publicTitle} onChange={v => handleLocalCampaignChange('publicTitle', v)} helpText="Visible to creators" />
                </BlockStack>
                <BlockStack gap="100">
                  <Text>Hero Image</Text>
                  {
                    localCampaign?.hero ? (
                      <>
                        <InlineStack gap="100" align="start">
                          <Image width="50%" source={localCampaign?.hero} />
                        </InlineStack>
                        <input type="file" accept="image/*" onChange={async e => {
                          const url = await uploadFile(e.target.files[0], localCampaign?.id || getRandId())
                          handleLocalCampaignChange('hero', url)
                        }} id="hero-upload" style={{ display: 'none' }} />
                        <label id="hero-label" for="hero-upload">
                        </label>
                        <InlineStack gap="100" align="start" blockAlign="center">
                          <Button loading={uploadLoading} id="upload-btn" onClick={_ => {
                            document.getElementById('hero-label').click()
                          }}>Change</Button>
                          <Select label="Position" labelInline options={[
                            { label: 'Top', value: 'top' },
                            { label: 'Center', value: 'center' },
                            { label: 'Bottom', value: 'bottom' }
                          ]} value={localCampaign?.heroPosition} onChange={v => handleLocalCampaignChange('heroPosition', v)} />
                          <Select label="Size" labelInline options={[
                            { label: 'Full Width', value: 'Full Width' },
                            { label: 'Full Height', value: 'Full Height' },
                            { label: 'Contain', value: 'contain' },
                            { label: 'Cover', value: 'cover' }
                          ]} value={localCampaign?.heroSize} onChange={v => handleLocalCampaignChange('heroSize', v)} />
                        </InlineStack>
                      </>
                    ) : (
                      <>
                        <input type="file" accept="image/*" onChange={async e => {
                          const url = await uploadFile(e.target.files[0], localCampaign?.id || getRandId())
                          handleLocalCampaignChange('hero', url)
                        }} id="hero-upload" style={{ display: 'none' }} />
                        <Text variant="subdued">Recommended Size: 1920x1080</Text>
                        <label id="hero-label" for="hero-upload">
                          <Button loading={uploadLoading} id="upload-btn" onClick={_ => {
                            document.getElementById('hero-label').click()
                          }}>Upload</Button>
                        </label>
                      </>
                    )
                  }
                </BlockStack>
              </InlineGrid>
              {/*
              <TextField label="Campaign Tags (Comma Seperated)" value={localCampaign?.tags} onChange={v => handleLocalCampaignChange('tags', v)} helpText="Ex. UGC, Influencer Marketing, VIP... Only visible to you." />
            */}
              <Divider />
              <BlockStack gap="100">
                <Text variant="headingMd">Overview</Text>
                <InstructionsRichTextEditor value={localCampaign?.specs?.overview} setValue={v => handleLocalCampaignChange('specs.overview', v)} />
              </BlockStack>
              <Divider />
              <InlineStack gap="500" align="start" blockAlign="center">
                <BlockStack gap="200">
                  <Text variant="headingMd">Auto-Approve</Text>
                  <InlineStack gap="300" align="start">
                    <RadioButton label="On" checked={localCampaign?.autoApprove} onChange={() => handleLocalCampaignChange('autoApprove', true)} />
                    <RadioButton label="Off" checked={!localCampaign?.autoApprove} onChange={() => handleLocalCampaignChange('autoApprove', false)} />
                  </InlineStack>
                </BlockStack>
                <BlockStack gap="200" inlineAlign="start">
                  <Text color="subdued" variant="subdued">This will automatically, if applicable...</Text>
                  <InlineStack gap="300">
                    <InlineStack gap="300">
                      <Tag>1</Tag>
                      <Text>Create a Discount Code</Text>
                    </InlineStack>
                    <InlineStack gap="300">
                      <Tag>2</Tag>
                      <Text>Create a Draft Order</Text>
                    </InlineStack>
                    <InlineStack gap="300">
                      <Tag>3</Tag>
                      <Text>Start the Welcome Email Sequence</Text>
                    </InlineStack>
                  </InlineStack>
                </BlockStack>
              </InlineStack>
            </BlockStack>
            <InlineStack align="end">
              <Button onClick={() => setOpenSection('content')}>Next</Button>
            </InlineStack>
          </Collapsible>
        </BlockStack>
      </Card>

      {
        <Card>
          <BlockStack gap="500">
            <Box onClick={() => openSection === 'content' ? setOpenSection(null) : setOpenSection('content')}>
              <InlineStack align="space-between">
                <InlineStack gap="100" blockAlign="center">
                  <InlineStack>
                    <Icon source={ImagesIcon} color="success" />
                  </InlineStack>
                  <Text variant="headingMd">Content</Text>
                </InlineStack>
                <Button icon={openSection === 'content' ? ChevronUpIcon : ChevronDownIcon} />
              </InlineStack>
            </Box>
            <Collapsible open={openSection === 'content'} id="content">
              <BlockStack gap="500">
                <InlineGrid columns="2fr 1fr" gap="500">
                  <ChoiceList title="Deliverables" choices={[
                    { label: 'Fixed Quantity', value: 'fixed', helpText: 'One time delivery' },
                    { label: 'Recurring', value: 'recurring', helpText: 'Receive ongoing deliveries, at the interval you choose' },
                    { label: 'Evergreen', value: 'evergreen', helpText: 'Open-ended, creator can post as much as they want' },
                  ]} selected={localCampaign?.content?.baseType} onChange={v => {
                    console.log('v is', v)
                    handleLocalCampaignChange('content.baseType', v?.[0])
                  }
                  } />
                  {
                    localCampaign?.content?.baseType !== 'evergreen' ? (
                      <BlockStack gap="300">
                        <Text>Timeline</Text>
                        <BlockStack align="start" gap="100">
                          <TextField
                            // helpText="Including product delivery time"
                            value={localCampaign?.content?.dueInDays}
                            onChange={v => handleLocalCampaignChange('content.dueInDays', v)}
                            min={1}
                            max={14}
                            suffix="day(s)"
                            prefix={<InlineStack gap="200">
                              {
                                /*
    <Icon color="subdued" source={ClockMajor} />
    */
                              }
                              {
                                localCampaign?.content?.baseType === 'recurring' ? (
                                  <Text variant="bodyMd">Due every</Text>
                                ) : <Text variant="bodyMd">Due in</Text>
                              }
                            </InlineStack>}
                          />
                        </BlockStack>
                        <TextField prefix="Delay Start By" suffix="Days" value={localCampaign?.content?.delayStartDays} onChange={v => handleLocalCampaignChange('content.delayStartDays', v)} />
                      </BlockStack>
                    ) : null
                  }
                </InlineGrid>
                <Divider />
                <Text>Format(s)</Text>
                <InlineGrid columns={{ xs: "1", md: "2" }} alignItems="center" gap="500">
                  {
                    localCampaign?.content?.formats?.map((formatItem, index) => {
                      console.log('formatItem', formatItem)
                      const format = Object.keys(formatItem)[0]
                      const quantity = formatItem[format]
                      return (
                        <Card key={index} >
                          <BlockStack gap="300" align="space-between" blockAlign="center">
                            <InlineStack align="space-between">
                              <InlineStack align="start" gap="300" blockAlign="center">
                                {
                                  contentFormats.find(item => item.value === format)?.icon
                                }
                                <Text variant="headingMd">
                                  {
                                    contentFormats.find(item => item.value === format)?.label
                                  }
                                </Text>
                              </InlineStack>
                              <Button onClick={() => handleLocalCampaignChange('content.formats',
                                localCampaign?.content?.formats?.filter(f => Object.keys(f)[0] !== format)
                              )} icon={XSmallIcon}></Button>
                            </InlineStack>
                            {
                              localCampaign?.content?.baseType !== 'evergreen' ? (
                                <TextField prefix="Quantity" value={quantity} onChange={v => handleLocalCampaignChange('content.formats',
                                  localCampaign?.content?.formats?.map(f => {
                                    if (Object.keys(f)[0] === format) {
                                      f[format] = v
                                    }
                                    return f
                                  })
                                )}
                                />
                              ) : null
                            }
                          </BlockStack>
                        </Card>
                      )
                    })
                  }
                </InlineGrid>
                <Divider />
                <Text>Add Format</Text>
                <InlineGrid gap="300" columns={{ xs: "2", md: "3" }}>
                  {
                    contentFormats.filter(f => {
                      return !localCampaign?.content?.formats?.find(format => format[f.value])
                    }
                    ).map((item, index) => (
                      <Button icon={item.icon} onClick={() => handleLocalCampaignChange(
                        'content.formats',
                        [...(localCampaign?.content?.formats || []), { [item.value]: 1 }]
                      )} key={index}>
                        {item.label}
                      </Button>
                    ))
                  }
                </InlineGrid>
                <BlockStack gap="200">
                  <BlockStack gap="300">
                    {
                      localCampaign?.content?.baseType !== 'evergreen' ? (
                        <>
                          <Divider />
                          <Text>Options</Text>
                          <InlineGrid columns="2" gap="500" alignItems="center">
                            <Checkbox label="Review Content Before Approval"
                              checked={localCampaign?.content?.reviewContent}
                              onChange={() => handleLocalCampaignChange('content.reviewContent', !localCampaign?.content?.reviewContent)}
                              helpText="Creator will upload files, you can send revisions before paying"
                            />
                            <Checkbox label="Send Email Reminders"
                              checked={localCampaign?.content?.sendReminders}
                              onChange={() => handleLocalCampaignChange('content.sendReminders', !localCampaign?.content?.sendReminders)}
                              helpText="Reminders will be sent 3 days before, 1 day before, and on due date"
                            />
                          </InlineGrid>
                        </>
                      ) : null
                    }
                  </BlockStack>
                </BlockStack>
                <Divider />
                <InlineStack align="end">
                  <ButtonGroup>
                    <Button onClick={() => setOpenSection('details')}>Prev</Button>
                    <Button onClick={() => setOpenSection('products')}>Next</Button>
                  </ButtonGroup>
                </InlineStack>
              </BlockStack>
            </Collapsible>
          </BlockStack>
        </Card>
      }
      <Card>
        <BlockStack gap="300">
          <Box onClick={() => openSection === 'payment' ? setOpenSection(null) : setOpenSection('payment')}>
            <InlineStack align="space-between">
              <InlineStack gap="100" blockAlign="center">
                <InlineStack>
                  <Icon source={CashDollarIcon} color="success" />
                </InlineStack>
                <Text variant="headingMd">Payment</Text>
              </InlineStack>
              <Button icon={openSection === 'payment' ? ChevronUpIcon : ChevronDownIcon} />
            </InlineStack>
          </Box>
          <Collapsible open={openSection === 'payment'} id="payment">
            <BlockStack gap="500">
              <BlockStack gap="500" inlineAlign="start">
                <InlineStack blockAlign="center" gap="100">
                  <Checkbox checked={localCampaign?.payments?.price} onChange={(v) => handleLocalCampaignChange('payments', { ...localCampaign?.payments, price: v ? 100 : 0 })} />
                  <Text variant="headingMd">Cash Payment</Text>
                  {/*
                  <Checkbox checked={localCampaign?.payments.value} onChange={() => {
                    handleLocalCampaignChange('payments.includePayment', !localCampaign?.payments.includePayment)
                  }
                  } />
                */}
                </InlineStack>
                <InlineGrid gap="200" columns="2">
                  <BlockStack gap="200">
                    <TextField
                      label="Payment Value"
                      prefix={<InlineStack gap="100">$</InlineStack>}
                      disabled={!localCampaign?.payments?.price}
                      suffix="USD"
                      value={localCampaign?.payments?.price}
                      onChange={v => handleLocalCampaignChange('payments.price', Number(v))}
                    />
                  </BlockStack>
                </InlineGrid>
              </BlockStack>
              <Divider />
              <BlockStack gap="500">
                <BlockStack inlineAlign="start" gap="500">
                  <InlineStack blockAlign="center" gap="100">
                    <Checkbox checked={localCampaign?.payments?.includeAffiliate} onChange={() => {
                      handleLocalCampaignChange('payments.includeAffiliate', !localCampaign?.payments?.includeAffiliate)
                    }} />
                    <Text variant="headingMd">Affiliate</Text>
                  </InlineStack>
                  <BlockStack gap="500">
                    <BlockStack gap="100" inlineAlign="start">
                      <Text>Discount Type</Text>
                      <InlineStack gap="200">
                        <RadioButton disabled={!localCampaign?.payments?.includeAffiliate} label="Percentage" checked={localCampaign?.affiliateFields?.discountType === 'percentage'} onChange={() => handleLocalCampaignChange('affiliateFields.discountType', 'percentage')} />
                        <RadioButton disabled={!localCampaign?.payments?.includeAffiliate} label="Fixed Amount" checked={localCampaign?.affiliateFields?.discountType === 'fixed'} onChange={() => handleLocalCampaignChange('affiliateFields.discountType', 'fixed')} />
                        <RadioButton disabled={!localCampaign?.payments?.includeAffiliate} label="Free Shipping" checked={localCampaign?.affiliateFields?.discountType === 'freeShipping'} onChange={() => handleLocalCampaignChange('affiliateFields.discountType', 'freeShipping')} />
                      </InlineStack>
                    </BlockStack>
                    <Divider />
                    <InlineGrid columns={{ xs: "1", md: "3" }} gap="500" alignItems="end">
                      <BlockStack gap="100">
                        <TextField
                          label="Commission"
                          disabled={!localCampaign?.payments?.includeAffiliate}
                          value={localCampaign?.affiliateFields?.commission}
                          onChange={v => handleLocalCampaignChange('affiliateFields.commission', v)}
                          suffix="% of revenue"
                        />
                      </BlockStack>
                      <BlockStack gap="100">
                        <TextField
                          label="Cookie Duration"
                          value={localCampaign?.affiliateFields?.cookieDuration}
                          onChange={v => handleLocalCampaignChange('affiliateFields.cookieDuration', v)}
                          suffix={'days'}
                        />
                      </BlockStack>
                      {localCampaign?.affiliateFields.discountType === 'percentage' ? (
                        <BlockStack gap="100">
                          <TextField
                            label="Discount Amount"
                            disabled={!localCampaign?.payments?.includeAffiliate}
                            value={localCampaign?.affiliateFields?.discountPercentage}
                            onChange={v => handleLocalCampaignChange('affiliateFields.discountPercentage', v)}
                            suffix={'% off'}
                          />
                        </BlockStack>
                      ) : localCampaign?.affiliateFields.discountType === 'fixed' ? (
                        <BlockStack gap="100">
                          <TextField
                            label="Discount Amount"
                            prefix="$"
                            disabled={!localCampaign?.payments?.includeAffiliate}
                            value={localCampaign?.affiliateFields?.discountFixedValue}
                            onChange={v => handleLocalCampaignChange('affiliateFields.discountFixedValue', v)}
                            suffix={'off'}
                          />
                        </BlockStack>
                      ) : localCampaign?.affiliateFields.discountType === 'freeShipping' ? (
                        <BlockStack gap="100" align="end">
                          <InlineStack gap="200">
                            <Checkbox label="US Only" checked={localCampaign?.affiliateFields?.usOnly}
                              onChange={() => handleLocalCampaignChange('affiliateFields.usOnly', !localCampaign?.affiliateFields?.usOnly)} />
                          </InlineStack>
                        </BlockStack>
                      ) : null}
                    </InlineGrid>
                  </BlockStack>
                </BlockStack>
                <Divider />

                {/*
                <BlockStack gap="300">
                  <BlockStack blockAlign="center" gap="200">
                    <Text variant="headingMd">PayPal</Text>
                    <Checkbox label="Link Up-Front" checked={localCampaign?.payments.linkPaypalUpfront} onChange={() => {
                      handleLocalCampaignChange('payments.linkPaypalUpfront', !localCampaign?.payments.linkPaypalUpfront)
                    }} />
                  </BlockStack>
                </BlockStack>
              */}
              </BlockStack>
              <InlineStack align="end">
                <ButtonGroup>
                  <Button onClick={() => setOpenSection('products')}>Prev</Button>
                  <Button onClick={() => setOpenSection('instructions')}>Next</Button>
                </ButtonGroup>
              </InlineStack>
            </BlockStack>
          </Collapsible>
        </BlockStack >
      </Card >
      <Card>
        <BlockStack gap="500">
          <Box onClick={() => openSection === 'instructions' ? setOpenSection(null) : setOpenSection('instructions')}>
            <InlineStack align="space-between">
              <InlineStack gap="100" blockAlign="center">
                <InlineStack>
                  <Icon source={TextAlignLeftIcon} color="success" />
                </InlineStack>
                <Text variant="headingMd">Instructions</Text>
              </InlineStack>
              <Button icon={openSection === 'instructions' ? ChevronUpIcon : ChevronDownIcon} />
            </InlineStack>
          </Box>
          <Collapsible open={openSection === 'instructions'} id="instructions">
            <BlockStack gap="500">
              <InstructionsRichTextEditor
                title="Do's and Dont's"
                helpText="Lines to be included? Excluded?"
                value={localCampaign?.specs?.script}
                setValue={v => handleLocalCampaignChange('script', v)}
              />
              <InstructionsRichTextEditor
                title="Creative Direction"
                helpText="Product demo vs review? Shooting location? Style?"
                value={localCampaign?.specs?.creativeDirection}
                setValue={v => handleLocalCampaignChange('creativeDirection', v)}
              />
              <InstructionsRichTextEditor
                title="Inspiration Examples"
                helpText="Links to videos or images that you like, and how to use them"
                value={localCampaign?.specs?.inspiration}
                setValue={v => handleLocalCampaignChange('inspiration', v)}
              />
              <BlockStack gap="300">
                <Text>Assets</Text>
                {
                  localCampaign?.assets?.length ? (
                    <InlineStack gap="500" align="start">
                      {
                        localCampaign?.assets?.map((asset, index) => {
                          return (
                            <InlineStack key={index} gap="100" blockAlign="center" align="start">
                              <InlineStack>
                                <Icon color="interactive" source={asset?.name?.includes('doc') || asset?.name?.includes('pdf') ? PageAddIcon : ImagesIcon} />
                              </InlineStack>
                              <Link removeUnderline url={asset.url}>{asset.name}</Link>
                              <Button loading={deleteAssetLoading} disabled={deleteAssetLoading && deleteAssetLoading !== index} plain onClick={async () => {
                                try {
                                  setDeleteAssetLoading(index)
                                  const fileRef = ref(storage, `${store?.shop}/campaignAssets/${asset.id}`);
                                  await deleteObject(fileRef);
                                  const newAssets = localCampaign.assets;
                                  newAssets.splice(index, 1);
                                  handleLocalCampaignChange('assets', newAssets);
                                } catch (e) {
                                  setToast({
                                    content: 'Could not delete asset'
                                  })
                                } finally {
                                  setDeleteAssetLoading(null)
                                }
                              }
                              } icon={XSmallIcon} />
                            </InlineStack>
                          )
                        })
                      }
                    </InlineStack>
                  ) : null
                }
                {
                  /*
                  <AssetUploader
                    addAsset={(newAsset) => handleLocalCampaignChange('assets', [
                      ...(localCampaign.assets || []),
                      newAsset,
                    ])}
                    campaignID={campaign?.id} open={assetUploaderOpen} onClose={() => setAssetUploaderOpen(false)} />
                    */
                }
                <ButtonGroup>
                  <Button disabled={localCampaign?.assets?.length > 4} onClick={() => {
                    setAssetUploaderOpen(true)
                  }} icon={LinkIcon}>Add Assets</Button>
                </ButtonGroup>
              </BlockStack>
              <Divider />
              <InlineStack align="end">
                <ButtonGroup>
                  <Button onClick={() => setOpenSection('payment')}>Prev</Button>
                  <Button onClick={() => setOpenSection('terms')}>Next</Button>
                </ButtonGroup>
              </InlineStack>
            </BlockStack>
          </Collapsible>
        </BlockStack>
      </Card>
      <Card>
        <BlockStack gap="500">
          <Box onClick={() => openSection === 'terms' ? setOpenSection(null) : setOpenSection('terms')}>
            <InlineStack align="space-between">
              <InlineStack gap="100" blockAlign="center">
                <InlineStack>
                  <Icon source={ContractIcon} color="success" />
                </InlineStack>
                <Text variant="headingMd">Content Terms & Rights</Text>
              </InlineStack>
              <Button icon={openSection === 'terms' ? ChevronUpIcon : ChevronDownIcon} />
            </InlineStack>
          </Box>
          <Collapsible open={openSection === 'terms'} id="terms">
            <BlockStack gap="500">
              <BlockStack gap="100">
                <Text variant="headingMd">Terms</Text>
                <InstructionsRichTextEditor value={localCampaign.contentRights?.terms} setValue={v => handleLocalCampaignChange('contentRights.terms', v)} />
              </BlockStack>
              <Checkbox label="Require Digital Signature" checked={localCampaign.contentRights?.digitalSignature} onChange={v => handleLocalCampaignChange('contentRights.digitalSignature', v)} />
              <Divider />
              <InlineStack align="end">
                <ButtonGroup>
                  <Button onClick={() => setOpenSection('instructions')}>Prev</Button>
                  <Button onClick={() => setOpenSection('form')}>Next</Button>
                </ButtonGroup>
              </InlineStack>
            </BlockStack>
          </Collapsible>
        </BlockStack>
      </Card>
      <Card>
        <BlockStack gap="500">
          <Box onClick={() => openSection === 'form' ? setOpenSection(null) : setOpenSection('form')}>
            <InlineStack align="space-between">
              <InlineStack gap="100" blockAlign="center">
                <InlineStack>
                  <Icon source={FormsIcon} color="success" />
                </InlineStack>
                <Text variant="headingMd">Custom Fields</Text>
              </InlineStack>
              <Button icon={openSection === 'form' ? ChevronUpIcon : ChevronDownIcon} />
            </InlineStack>
          </Box>
          <Collapsible open={openSection === 'form'} id="form">
            <BlockStack gap="500">
              <BlockStack gap="300">
                <BlockStack gap="100">
                  <Text variant="headingMd">Custom Fields</Text>
                  <Text color="subdued">Optional. Collect additional information from creators.</Text>
                </BlockStack>
                <InlineGrid columns={{ xs: "1", md: "3" }} gap="200">
                  <Button onClick={() => handleLocalCampaignChange('form.customFields', [...(localCampaign?.form?.customFields || []), { label: "", type: "text" }])}>Add Text Field</Button>
                  <Button onClick={() => handleLocalCampaignChange('form.customFields', [...(localCampaign?.form?.customFields || []), { label: "", type: "checkbox", options: [] }])}>Add Checkbox</Button>
                  <Button onClick={() => handleLocalCampaignChange('form.customFields', [...(localCampaign?.form?.customFields || []), { label: "", type: "select", options: [] }])}>Add Dropdown</Button>
                </InlineGrid>
                <BlockStack gap="500">
                  {
                    localCampaign?.form?.customFields?.map((field, index) => {
                      return (
                        <Card background="bg-subdued" key={index} gap="500">
                          <BlockStack gap="500">
                            <InlineStack align="space-between">
                              <InlineStack gap="500">
                                <BlockStack>
                                  <Text variant="subdued">Custom Field {index + 1}</Text>
                                  <InlineStack>
                                    <Badge>{field.type} field</Badge>
                                  </InlineStack>
                                </BlockStack>
                                <Checkbox label="Required?" checked={field.required} onChange={v => {
                                  const newFields = localCampaign?.form.customFields;
                                  newFields[index].required = v;
                                  handleLocalCampaignChange('form.customFields', newFields);
                                }} />
                                {
                                  field.type === "text" ? (
                                    <Checkbox label="Multi Line?" checked={field.multiline} onChange={v => {
                                      const newFields = localCampaign?.form.customFields;
                                      newFields[index].multiline = v;
                                      handleLocalCampaignChange('form.customFields', newFields);
                                    }} />
                                  ) : null
                                }
                              </InlineStack>
                              <Button onClick={() => {
                                const newFields = localCampaign?.form.customFields;
                                newFields.splice(index, 1);
                                handleLocalCampaignChange('form.customFields', newFields);
                              }} icon={XSmallIcon} />
                            </InlineStack>
                            {
                              field.type === "text" ? (
                                <>
                                  <TextField label="Label" value={field.label} onChange={v => {
                                    const newFields = localCampaign?.form.customFields;
                                    newFields[index].label = v;
                                    handleLocalCampaignChange('form.customFields', newFields);
                                  }}
                                  />
                                </>
                              ) : field.type === "checkbox" ? (
                                <>
                                  <TextField label="Label" value={field.label} onChange={v => {
                                    const newFields = localCampaign?.form.customFields;
                                    newFields[index].label = v;
                                    handleLocalCampaignChange('form.customFields', newFields);
                                  }} />
                                  {
                                    <BlockStack gap="300">
                                      {field.options?.map((option, optionIndex) => {
                                        return (
                                          <TextField label={'Checkbox ' + (optionIndex + 1)} value={option} onChange={v => {
                                            const newFields = localCampaign?.form.customFields;
                                            newFields[index].options[optionIndex] = v;
                                            handleLocalCampaignChange('form.customFields', newFields);
                                          }} suffix={
                                            <Button icon={XSmallIcon} onClick={() => {
                                              const newFields = localCampaign?.form.customFields;
                                              newFields[index].options.splice(optionIndex, 1);
                                              handleLocalCampaignChange('form.customFields', newFields);
                                            }} />
                                          } />
                                        )
                                      })}
                                      <InlineStack align="start">
                                        <Button onClick={() => {
                                          const newFields = localCampaign?.form.customFields;
                                          newFields[index].options.push('');
                                          handleLocalCampaignChange('form.customFields', newFields);
                                        }}>Add Option</Button>
                                      </InlineStack>
                                    </BlockStack>
                                  }
                                </>
                              ) : field.type === "select" ? (
                                <>
                                  <TextField label="Label" value={field.label} onChange={v => {
                                    const newFields = localCampaign?.form.customFields;
                                    newFields[index].label = v;
                                    handleLocalCampaignChange('form.customFields', newFields);
                                  }
                                  } />
                                  <BlockStack gap="300">
                                    {field.options?.map((option, optionIndex) => {
                                      return (
                                        <TextField label={'Option ' + (optionIndex + 1)} value={option} onChange={v => {
                                          const newFields = localCampaign?.form.customFields;
                                          newFields[index].options[optionIndex] = v;
                                          handleLocalCampaignChange('form.customFields', newFields);
                                        }} suffix={
                                          <Button icon={XSmallIcon} onClick={() => {
                                            const newFields = localCampaign?.form.customFields;
                                            newFields[index].options.splice(optionIndex, 1);
                                            handleLocalCampaignChange('form.customFields', newFields);
                                          }} />
                                        } />
                                      )
                                    })}
                                    <InlineStack align="start">
                                      <Button onClick={() => {
                                        const newFields = localCampaign?.form.customFields;
                                        newFields[index].options.push('');
                                        handleLocalCampaignChange('form.customFields', newFields);
                                      }}>Add Option</Button>
                                    </InlineStack>
                                  </BlockStack>
                                </>
                              ) : null
                            }
                          </BlockStack>
                        </Card>
                      )
                    })
                  }
                </BlockStack>
              </BlockStack>
            </BlockStack>
          </Collapsible>
        </BlockStack>
      </Card>
    </BlockStack >
  )
}
